import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Search, Filter, ArrowUp, ArrowDown, ArrowUpDown, User, ImageOff, ImageIcon, ChevronsUp, ChevronsDown } from "lucide-react";
import { useNavigate } from "react-router-dom";

const FILTERS = [
  { key: "all", label: "Tümü" },
  { key: "misas", label: "Misaş" },
  { key: "mis_holding", label: "Mis Holding" },
  { key: "koop", label: "Mis Koop." },
];

const PHOTO_FILTERS = [
  { key: "all", label: "Fotoğraf: Tümü", icon: ImageIcon },
  { key: "yes", label: "Sadece fotoğraflı", icon: ImageIcon },
  { key: "no",  label: "Fotoğrafsız",       icon: ImageOff },
];

function initials(name) {
  return (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map(x => x[0])
    .join("")
    .toLocaleUpperCase("tr-TR") || "?";
}

function Avatar({ src, name }) {
  if (src) {
    return (
      <img
        src={src}
        alt={name || ""}
        loading="lazy"
        className="h-9 w-9 rounded-full object-cover ring-1 ring-slate-200"
      />
    );
  }
  return (
    <div className="h-9 w-9 rounded-full bg-slate-100 border border-dashed border-slate-300 flex items-center justify-center text-[10px] font-bold text-slate-400 tracking-wide">
      {initials(name)}
    </div>
  );
}

function ScrollButtons() {
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 400);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const toTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const toBottom = () => window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });

  return (
    <div className="fixed right-4 sm:right-6 bottom-4 sm:bottom-6 z-40 flex flex-col gap-2">
      <button
        type="button"
        data-testid="scroll-top-btn"
        onClick={toTop}
        aria-label="En yukarı çık"
        title="En yukarı çık"
        className={`h-11 w-11 rounded-full bg-slate-900 text-white shadow-lg hover:bg-slate-800 flex items-center justify-center transition-all ${showTop ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2 pointer-events-none"}`}
      >
        <ChevronsUp size={20} />
      </button>
      <button
        type="button"
        data-testid="scroll-bottom-btn"
        onClick={toBottom}
        aria-label="En alta in"
        title="En alta in"
        className="h-11 w-11 rounded-full bg-slate-900 text-white shadow-lg hover:bg-slate-800 flex items-center justify-center"
      >
        <ChevronsDown size={20} />
      </button>
    </div>
  );
}

export default function Members() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState("all");
  const [photoFilter, setPhotoFilter] = useState("all");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ col: "ad_soyad", dir: "asc" });
  const navigate = useNavigate();

  const fetchData = () => {
    setLoading(true);
    const params = { status: "active" };
    if (company !== "all") params.company = company;
    if (photoFilter !== "all") params.has_photo = photoFilter;
    if (q.trim()) params.q = q.trim();
    axios.get(`${API}/members`, { params }).then(r => setRows(r.data)).finally(() => setLoading(false));
  };

  useEffect(fetchData, [company, photoFilter]);
  useEffect(() => {
    const t = setTimeout(fetchData, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const sortedRows = useMemo(() => {
    const arr = [...rows];
    const col = sort.col;
    const dir = sort.dir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      if (col === "ad_soyad") {
        return (a.ad_soyad || "").localeCompare(b.ad_soyad || "", "tr") * dir;
      }
      const av = Number(a[col] || 0);
      const bv = Number(b[col] || 0);
      if (av === bv) return (a.ad_soyad || "").localeCompare(b.ad_soyad || "", "tr");
      return (av - bv) * dir;
    });
    return arr;
  }, [rows, sort]);

  const totals = useMemo(() => {
    const t = { misas: 0, mis_holding: 0, koop: 0, withPhoto: 0 };
    for (const m of rows) {
      t.misas += Number(m.misas_hisse || 0);
      t.mis_holding += Number(m.mis_holding_hisse || 0);
      t.koop += Number(m.koop_hisse || 0);
      if (m.photo_thumb) t.withPhoto++;
    }
    return t;
  }, [rows]);

  const fmt = (n) => Number(n).toLocaleString("tr-TR");

  const toggleSort = (col) => {
    setSort(s => {
      if (s.col !== col) {
        return { col, dir: col === "ad_soyad" ? "asc" : "desc" };
      }
      return { col, dir: s.dir === "asc" ? "desc" : "asc" };
    });
  };

  const SortIcon = ({ col }) => {
    if (sort.col !== col) return <ArrowUpDown size={12} className="inline text-slate-300 ml-1" />;
    return sort.dir === "asc"
      ? <ArrowUp size={12} className="inline text-slate-900 ml-1" />
      : <ArrowDown size={12} className="inline text-slate-900 ml-1" />;
  };

  const HeaderBtn = ({ col, children, align = "left", testid }) => (
    <button
      type="button"
      data-testid={testid}
      onClick={() => toggleSort(col)}
      className={`w-full inline-flex items-center gap-0.5 ${align === "right" ? "justify-end" : "justify-start"} font-bold uppercase tracking-wider text-slate-500 hover:text-slate-900`}
    >
      {children}<SortIcon col={col} />
    </button>
  );

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-2">Kayıtlar</div>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Ortaklar</h1>
        <p className="text-slate-600 mt-1 text-sm">Ad, TC ile arayın veya şirkete göre filtreleyin. Sütun başlığına tıklayarak hisse veya ada göre sıralayın.</p>
      </div>

      <div className="crisp-card p-4 flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="members-search"
            value={q} onChange={e => setQ(e.target.value)}
            placeholder="Ad Soyad veya TC ile ara..."
            className="w-full pl-9 pr-3 py-2.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Filter size={15} className="text-slate-400" />
          {FILTERS.map(f => (
            <button
              key={f.key}
              data-testid={`filter-${f.key}`}
              onClick={() => setCompany(f.key)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                company === f.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              }`}
            >{f.label}</button>
          ))}
        </div>
      </div>

      <div className="crisp-card p-3 flex items-center gap-2 flex-wrap">
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mr-2">Fotoğraf:</span>
        {PHOTO_FILTERS.map(f => {
          const Icon = f.icon;
          return (
            <button key={f.key}
              data-testid={`photo-filter-${f.key}`}
              onClick={() => setPhotoFilter(f.key)}
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${photoFilter === f.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
              <Icon size={12}/> {f.label}
            </button>
          );
        })}
        <div className="ml-auto text-[11px] text-slate-500 font-mono-nums">
          <span data-testid="photo-count" className="font-semibold text-slate-700">{totals.withPhoto}</span> / {rows.length} fotoğraflı
        </div>
      </div>

      <div className="crisp-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[11px]">
                <th className="pl-4 pr-2 py-3 w-14"></th>
                <th className="px-4 py-3">
                  <HeaderBtn col="ad_soyad" testid="sort-ad-soyad">Ad Soyad</HeaderBtn>
                </th>
                <th className="px-4 py-3 font-bold uppercase tracking-wider text-slate-500">TC Kimlik</th>
                <th className="px-4 py-3 font-bold uppercase tracking-wider text-slate-500">Telefon</th>
                <th className="px-4 py-3 text-right">
                  <HeaderBtn col="misas_hisse" align="right" testid="sort-misas">Misaş</HeaderBtn>
                </th>
                <th className="px-4 py-3 text-right">
                  <HeaderBtn col="mis_holding_hisse" align="right" testid="sort-mis-holding">Mis Holding</HeaderBtn>
                </th>
                <th className="px-4 py-3 text-right">
                  <HeaderBtn col="koop_hisse" align="right" testid="sort-koop">Mis Koop.</HeaderBtn>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">Yükleniyor...</td></tr>
              )}
              {!loading && sortedRows.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">Kayıt bulunamadı.</td></tr>
              )}
              {!loading && sortedRows.map((m, i) => (
                <tr key={m.id}
                    data-testid={`member-row-${m.id}`}
                    onClick={() => navigate(`/ortak/${m.id}`)}
                    className="border-b border-slate-100 last:border-0 table-row-hover cursor-pointer stagger-row"
                    style={{ animationDelay: `${Math.min(i,20)*20}ms` }}>
                  <td className="pl-4 pr-2 py-2" data-testid={`member-avatar-${m.id}`}>
                    <Avatar src={m.photo_thumb} name={m.ad_soyad} />
                  </td>
                  <td className="px-4 py-3 font-semibold text-slate-900">{m.ad_soyad}</td>
                  <td className="px-4 py-3 text-slate-600 font-mono-nums">{m.tc_kimlik || "—"}</td>
                  <td className="px-4 py-3 text-slate-600 font-mono-nums">{m.gsm1 || m.tel1 || m.gsm2 || m.tel2 || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    {m.misas_hisse > 0 ? (
                      <span className="inline-flex company-misas rounded-full px-2.5 py-0.5 text-xs font-bold font-mono-nums">{m.misas_hisse}</span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {m.mis_holding_hisse > 0 ? (
                      <span className="inline-flex company-mh rounded-full px-2.5 py-0.5 text-xs font-bold font-mono-nums">{m.mis_holding_hisse}</span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {m.koop_hisse > 0 ? (
                      <span className="inline-flex company-koop rounded-full px-2.5 py-0.5 text-xs font-bold font-mono-nums">{m.koop_hisse}</span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
            {!loading && sortedRows.length > 0 && (
              <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                <tr className="text-[11px] font-bold uppercase tracking-wider text-slate-600">
                  <td className="px-4 py-3" colSpan={4}>Toplam Hisse</td>
                  <td className="px-4 py-3 text-right" data-testid="total-misas">
                    <span className="inline-flex company-misas rounded-full px-3 py-1 text-xs font-extrabold font-mono-nums">{fmt(totals.misas)}</span>
                  </td>
                  <td className="px-4 py-3 text-right" data-testid="total-mis-holding">
                    <span className="inline-flex company-mh rounded-full px-3 py-1 text-xs font-extrabold font-mono-nums">{fmt(totals.mis_holding)}</span>
                  </td>
                  <td className="px-4 py-3 text-right" data-testid="total-koop">
                    <span className="inline-flex company-koop rounded-full px-3 py-1 text-xs font-extrabold font-mono-nums">{fmt(totals.koop)}</span>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 text-xs text-slate-500 flex items-center justify-between gap-2 flex-wrap">
          <div>Toplam: <span className="font-semibold text-slate-800 font-mono-nums" data-testid="total-count">{sortedRows.length}</span> kayıt</div>
          <div className="text-slate-400">
            Sıralama: <span className="font-semibold text-slate-700">
              {sort.col === "ad_soyad" ? "Ad Soyad" : sort.col === "misas_hisse" ? "Misaş" : sort.col === "mis_holding_hisse" ? "Mis Holding" : "Mis Koop."}
            </span> · {sort.dir === "asc" ? "Küçükten büyüğe / A→Z" : "Büyükten küçüğe / Z→A"}
          </div>
        </div>
      </div>

      <ScrollButtons />
    </div>
  );
}
